import {
  DataModelAttribute,
  DataModelAttributeType,
  DataModelEntityType,
  DataModelUiEntity,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { computeDataModelDiff } from '@flowx/designer/modules/data-model-builder/react/utils/data-model-diff'
import {
  GeneratedDataModel,
  parseGeneratedDataModel,
} from '@flowx/designer/modules/data-model-builder/utils/generated-data-model.parser'
import { ProxyApiAction } from '@flowx/designer/shared/entities/application.entity'

import {
  ArchitectApplyAttribute,
  ArchitectApplyEntity,
  ArchitectApplyPayload,
  ArchitectApplyReference,
  ArchitectApplyResourceIds,
} from '../api/architect.api'

export class AiArchitectService {
  static buildApplyPayload(
    proposed: GeneratedDataModel,
    existing: DataModelUiEntity[]
  ): ArchitectApplyPayload {
    const parsed = parseGeneratedDataModel(proposed, DataModelEntityType.PROJECT, existing)
    const diff = computeDataModelDiff(existing, parsed)

    const affected = diff.mergedEntities.filter((entity) => {
      const status = diff.entityStatus.get(entity.identifier)
      return status === 'added' || status === 'modified'
    })

    const affectedByResourceDefinitionId = new Map(
      affected
        .filter((entity) => !!entity.resourceDefinitionId)
        .map((entity) => [entity.resourceDefinitionId, entity])
    )

    const transformReference = (
      reference?: DataModelAttribute['reference']
    ): ArchitectApplyReference | undefined => {
      if (!reference) {
        return undefined
      }
      const intraBatchTarget = reference.targetResourceDefinitionId
        ? affectedByResourceDefinitionId.get(reference.targetResourceDefinitionId)
        : undefined
      // intra-batch references go out as targetFlowxUuid so the BE remaps them to the final
      // resourceDefinitionId by name; references to untouched existing types pass through.
      return intraBatchTarget
        ? { targetFlowxUuid: intraBatchTarget.identifier }
        : {
            targetFlowxUuid: reference.targetFlowxUuid,
            targetResourceDefinitionId: reference.targetResourceDefinitionId,
            appId: reference.appId,
          }
    }

    const toApplyAttribute = (attribute: DataModelAttribute): ArchitectApplyAttribute => ({
      flowxUuid: attribute.flowxUuid,
      name: attribute.name,
      type: attribute.type,
      multiInstance: !!attribute.multiInstance,
      description: attribute.description,
      exampleValue: attribute.exampleValue,
      useInReporting: attribute.useInReporting,
      isSensitive: attribute.isSensitive,
      reference:
        attribute.type === DataModelAttributeType.OBJECT
          ? transformReference(attribute.reference)
          : undefined,
    })

    const dataEntities: ArchitectApplyEntity[] = affected.map((entity) => ({
      flowxUuid: entity.identifier,
      name: entity.name,
      description: entity.description,
      exampleValue: entity.exampleValue,
      attributes: entity.attributes.map(toApplyAttribute),
    }))

    const newResourceNames = affected
      .filter((entity) => diff.entityStatus.get(entity.identifier) === 'added')
      .map((entity) => entity.name)

    const existingResources = affected
      .filter((entity) => diff.entityStatus.get(entity.identifier) === 'modified')
      .reduce<Record<string, ArchitectApplyResourceIds>>((acc, entity) => {
        acc[entity.name] = {
          resourceId: entity.identifier,
          resourceDefinitionId: entity.resourceDefinitionId,
        }
        return acc
      }, {})

    return {
      newResourceNames,
      existingResources,
      action: ProxyApiAction.CREATE,
      protectExisting: true,
      payload: { exportVersion: 1, dataEntities },
    }
  }
}
